'use client';

import type { TrackCustomParams } from './useAnalyticsEvent';

/**
 * PDP review section event contract (SSD-5573) — web and mobile MUST emit the same
 * event name and property shape, so every trigger site goes through this module and
 * no component spells an event name itself.
 *
 * Spec: Notion "Review system Data Logging" (3a905a34aa25805dbac5f3db344cd5c7).
 *
 * Two spec events keep an existing name instead of taking the spec's, because they
 * already carry production history and renaming would break the series:
 *
 * - `review_tab_clicked`    → `section_click` (section_name `pdp_section_tabs`, element `reviews`)
 * - `review_section_viewed` → `section_impression` (section_name `reviews`)
 *
 * Those two are emitted by the section-tracking helpers each app already owns; this
 * module only supplies the shared properties for them via {@link reviewEventBaseParams}.
 * Everything else below is emitted under the spec name verbatim.
 *
 * `skipGTM: true` throughout — these are server-side analytics with no dataLayer
 * consumer, matching the sibling contracts in `frontInteractionEvents.ts`.
 */

type TrackCustomFn = (params: TrackCustomParams) => void;

/** Which region of the review section an event belongs to. Entry events carry no block. */
export type ReviewBlock = 'review_list' | 'ai_summary';

export interface ReviewEventBase {
  productId: string;
  /**
   * Left undefined for entry events, which are not scoped to a block. They still send
   * `block: null` — the spec defines that value, and an explicit null is easier to
   * query than a missing key.
   */
  block?: ReviewBlock;
  /**
   * Whether the product has at least one first-party review.
   *
   * Leave undefined while the reviews query is still loading or has errored — the
   * property is then omitted rather than sent as `false`. A failed request is not
   * evidence of zero reviews, and absence is easier to filter out downstream than a
   * value that silently means "we did not know".
   */
  hasFirstPartyReview?: boolean;
}

/**
 * The spec's per-event global properties, in payload shape.
 *
 * `session_id` / `user_id` / `platform` / `timestamp` are NOT here — `useAnalyticsEvent`
 * already attaches them to every EventAggregator event.
 *
 * Exported because the two exception events ride the existing `section_impression` /
 * `section_click` helpers and need to merge these into their own params.
 */
export const reviewEventBaseParams = ({
  productId,
  block,
  hasFirstPartyReview,
}: ReviewEventBase): Record<string, unknown> => ({
  product_id: productId,
  // Explicit null, not a missing key: the spec defines `null` as the entry-event value,
  // and `block IS NULL` has to match in the warehouse.
  block: block ?? null,
  ...(hasFirstPartyReview === undefined ? {} : { has_first_party_review: hasFirstPartyReview }),
});

const emit = (
  trackCustom: TrackCustomFn,
  eventName: string,
  base: ReviewEventBase,
  params?: Record<string, unknown>,
): void => {
  trackCustom({
    eventName,
    // base spread last so a stray caller param can never overwrite the typed globals.
    params: { ...params, ...reviewEventBaseParams(base) },
    skipGTM: true,
  });
};

/**
 * Fires once per session per product, the first time an event with that key is emitted.
 *
 * Module scope rather than `sessionStorage`: it behaves identically on web and React
 * Native, where `sessionStorage` does not exist. Its lifetime is the JS context — the
 * SPA session on web, the app session on mobile — which is the session the spec means.
 */
const firedOnceKeys = new Set<string>();

const oncePerSessionKey = (eventName: string, base: ReviewEventBase): string => `${eventName}:${base.productId}`;

const emitOncePerSession = (
  trackCustom: TrackCustomFn,
  eventName: string,
  base: ReviewEventBase,
  params?: Record<string, unknown>,
): void => {
  const key = oncePerSessionKey(eventName, base);

  if (firedOnceKeys.has(key)) {
    return;
  }

  firedOnceKeys.add(key);
  emit(trackCustom, eventName, base, params);
};

/** Test-only: clears the once-per-session ledger so cases do not leak into each other. */
export const __resetReviewSectionSessionState = (): void => {
  firedOnceKeys.clear();
};

// --- A. Entry (no block) ------------------------------------------------------------

/**
 * How the user arrived at the review section. `review_section_viewed` carries this so a
 * low `tab_click` share can be read as a tab placement/label problem rather than
 * disinterest in reviews.
 */
export type ReviewSectionEntry = 'tab_click' | 'organic_scroll' | 'anchor';

/** The section-tracking identities the two exception events already ship under. */
const SECTION_IMPRESSION_EVENT = 'section_impression';
const SECTION_CLICK_EVENT = 'section_click';
const PDP_PAGE = 'pdp';
const REVIEWS_SECTION_NAME = 'reviews';
const PDP_SECTION_TABS_SECTION_NAME = 'pdp_section_tabs';
const REVIEWS_TAB_ELEMENT = 'reviews';

/**
 * The spec's `review_section_viewed`, emitted under the existing `section_impression`
 * name because that event has been in production since 2026-07-14.
 *
 * Fires once per mount rather than once per session, which is the cadence already
 * shipped — changing how often a live event fires would distort its own series just as
 * much as renaming it would.
 *
 * `skipGTM` is deliberately absent: the shipped call omits it too, and `trackCustom`
 * only pushes to the dataLayer when a `gtmEventName` is present.
 */
export const trackReviewSectionViewed = (
  trackCustom: TrackCustomFn,
  base: ReviewEventBase,
  entry: ReviewSectionEntry,
): void => {
  trackCustom({
    eventName: SECTION_IMPRESSION_EVENT,
    params: {
      section_name: REVIEWS_SECTION_NAME,
      page: PDP_PAGE,
      entry,
      ...reviewEventBaseParams(base),
    },
  });
};

/**
 * The spec's `review_tab_clicked`, emitted under the existing `section_click` name for
 * the same reason. Only the Reviews tab routes here; the other tabs keep their plain
 * `section_click` with no review properties attached.
 */
export const trackReviewTabClicked = (trackCustom: TrackCustomFn, base: ReviewEventBase): void => {
  trackCustom({
    eventName: SECTION_CLICK_EVENT,
    params: {
      section_name: PDP_SECTION_TABS_SECTION_NAME,
      page: PDP_PAGE,
      element: REVIEWS_TAB_ELEMENT,
      ...reviewEventBaseParams(base),
    },
  });
};

/** Total time spent in the review section, emitted on exit. */
export const trackReviewSectionDwell = (trackCustom: TrackCustomFn, base: ReviewEventBase, dwellMs: number): void =>
  emit(trackCustom, 'review_section_dwell', base, { dwell_ms: dwellMs });

// --- B. Review list (block = review_list) -------------------------------------------

export type ReviewFilterType = 'sort_by' | 'skin_type' | 'skin_concern';

export const trackReviewFilterUsed = (
  trackCustom: TrackCustomFn,
  base: ReviewEventBase,
  filterType: ReviewFilterType,
  filterValue: string,
): void => emit(trackCustom, 'review_filter_used', base, { filter_type: filterType, filter_value: filterValue });

/** "View More" on a review card. */
export const trackReviewCardExpanded = (trackCustom: TrackCustomFn, base: ReviewEventBase, reviewId: string): void =>
  emit(trackCustom, 'review_card_expanded', base, { review_id: reviewId });

/** A review card's image carousel moved to a new index. */
export const trackReviewImageSwiped = (
  trackCustom: TrackCustomFn,
  base: ReviewEventBase,
  reviewId: string,
  toIndex: number,
): void => emit(trackCustom, 'review_image_swiped', base, { review_id: reviewId, to_index: toIndex });

/** "Load more reviews" — `page` is the page number being loaded. */
export const trackLoadMoreReviews = (trackCustom: TrackCustomFn, base: ReviewEventBase, page: number): void =>
  emit(trackCustom, 'load_more_reviews', base, { page });

// --- C. AI summary (block = ai_summary) ---------------------------------------------

const AI_SUMMARY_VIEWED_EVENT = 'ai_summary_viewed';
const AI_SUMMARY_READ_EVENT = 'ai_summary_read';

/** AI summary card reached 50% of the viewport. Once per session per product. */
export const trackAiSummaryViewed = (trackCustom: TrackCustomFn, base: ReviewEventBase): void =>
  emitOncePerSession(trackCustom, AI_SUMMARY_VIEWED_EVENT, base);

/** Which signal proved the summary was read, not merely seen. */
export type AiSummaryReadSignal = 'dwell' | 'scroll' | 'swipe';

/**
 * The first of: 2s dwell, scrolling past Good to Know, or swiping an insight carousel.
 * Once per session per product — later signals do not re-fire it.
 *
 * A summary that was never seen cannot have been read (SSD-5692): until
 * `ai_summary_viewed` has fired for this session+product, every read signal is dropped.
 * A dropped read does not spend the once-per-session slot, so a later signal that
 * arrives after the view still emits.
 */
export const trackAiSummaryRead = (
  trackCustom: TrackCustomFn,
  base: ReviewEventBase,
  readSignal: AiSummaryReadSignal,
): void => {
  if (!firedOnceKeys.has(oncePerSessionKey(AI_SUMMARY_VIEWED_EVENT, base))) {
    return;
  }

  emitOncePerSession(trackCustom, AI_SUMMARY_READ_EVENT, base, { read_signal: readSignal });
};

export type AiInsightGroup = 'most_loved' | 'good_to_know';

/**
 * An insight carousel moved to a new card. Callers must only emit on an actual index
 * change — a scroll that lands back on the same card is not a swipe.
 */
export const trackAiInsightCardSwiped = (
  trackCustom: TrackCustomFn,
  base: ReviewEventBase,
  insightGroup: AiInsightGroup,
  fromIndex: number,
  toIndex: number,
): void =>
  emit(trackCustom, 'ai_insight_card_swiped', base, {
    insight_group: insightGroup,
    from_index: fromIndex,
    to_index: toIndex,
  });

export type AiSummaryVote = 'helpful' | 'not_helpful';

export const trackAiSummaryHelpfulVote = (
  trackCustom: TrackCustomFn,
  base: ReviewEventBase,
  vote: AiSummaryVote,
): void => emit(trackCustom, 'ai_summary_helpful_vote', base, { vote });
